/**
 * Browser/desktop web-push client helpers (Phase 3).
 *
 * Thin wrapper around Firebase Cloud Messaging for the settings UI. The actual
 * background display + click routing lives in the service worker (`src/sw.ts`);
 * this module only handles the foreground opt-in: request permission (from a
 * user gesture) and mint an FCM token to register with the backend.
 */

import { getFirebaseMessaging, isFirebaseAppConfigured } from './firebase';
import { getToken } from 'firebase/messaging';

/** The Web Push certificate (public VAPID key) from Firebase → Cloud Messaging. */
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

/**
 * Whether this browser exposes the APIs desktop push needs. Purely a
 * capability check — says nothing about permission or whether push is
 * configured for this environment.
 */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Whether desktop push is actually usable here: the browser supports it, the
 * Firebase app is configured, and a VAPID key is present. When false, the UI
 * shows an informational "not set up for this environment" state rather than a
 * button that can't work.
 *
 * Checks `isFirebaseAppConfigured()` and NOT `isFirebaseConfigured()` — push
 * needs the Firebase app, not Firebase *auth*, so it must not be disabled by
 * the `VITE_FORCE_TEST_USER` auth bypass. An env with no VAPID key (dev, by
 * default) is still correctly "not configured" via the `VAPID_KEY` check.
 */
export function isPushConfigured(): boolean {
  return isPushSupported() && isFirebaseAppConfigured() && !!VAPID_KEY;
}

/** Current browser notification permission (`default` | `granted` | `denied`). */
export function getPermissionState(): NotificationPermission {
  if (!isPushSupported()) return 'denied';
  return Notification.permission;
}

/**
 * The CURRENT FCM token for this browser, or null when push isn't usable here
 * or permission was never granted. Never prompts (safe to call on load).
 *
 * Why this exists: FCM tokens are bound to the browser's push subscription,
 * which Chrome rotates (SW updates, cleared site data, key rotation). A send to
 * a stale token still returns 200 from FCM but is SILENTLY DROPPED — the
 * documented failure mode (firebase.blog "managing cloud messaging tokens").
 * getToken() re-validates/resubscribes against the live subscription, so
 * calling this on every app load and re-registering keeps the server's token
 * in lockstep with the browser.
 */
export async function getCurrentPushToken(): Promise<string | null> {
  if (!isPushConfigured() || getPermissionState() !== 'granted') return null;

  const messaging = await getFirebaseMessaging();
  if (!messaging) return null;

  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  return token || null;
}

/**
 * Turn on desktop push for this browser and return the FCM token to register.
 *
 * MUST be invoked from a user gesture (the card's button) — `requestPermission`
 * only prompts inside one, and browsers ignore it on load. Throws a clear Error
 * when permission is refused or the token can't be minted so the caller can toast it.
 */
export async function enableDesktopPush(): Promise<{ token: string }> {
  if (!isPushConfigured()) {
    throw new Error('Desktop notifications are not available in this browser.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked. Re-enable them in your browser site settings.'
        : 'Notification permission was not granted.',
    );
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    throw new Error('Push messaging is unavailable in this browser.');
  }

  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    throw new Error('Could not obtain a push token. Please try again.');
  }

  return { token };
}
